import { useEffect, useRef } from 'react';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  highlight?: string;
  warning?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const TONE_META: Record<NonNullable<ConfirmModalProps['tone']>, {
  icon: string;
  gradient: string;
  accent: string;
  ringColor: string;
}> = {
  danger: {
    icon: '🗑️',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
    accent: '#b91c1c',
    ringColor: 'rgba(239, 68, 68, 0.25)',
  },
  warning: {
    icon: '⚠️',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    accent: '#b45309',
    ringColor: 'rgba(245, 158, 11, 0.25)',
  },
  info: {
    icon: 'ℹ️',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    accent: '#4f46e5',
    ringColor: 'rgba(99, 102, 241, 0.25)',
  },
};

export function ConfirmModal({
  open,
  title,
  message,
  highlight,
  warning,
  confirmLabel = 'Onayla',
  cancelLabel = 'İptal',
  tone = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const meta = TONE_META[tone];
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (loading) return;
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, loading, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={loading ? undefined : onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 'clamp(2rem, 8vh, 6rem) 1rem 2rem',
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
        animation: 'cm-fade-in 0.18s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '28rem',
          background: 'white',
          borderRadius: '1.25rem',
          boxShadow:
            '0 25px 50px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          animation: 'cm-slide-down 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            background: meta.gradient,
            padding: '1.5rem 1.5rem 1.25rem',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 56,
              height: 56,
              margin: '0 auto',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.75rem',
              boxShadow: `0 6px 18px ${meta.ringColor}`,
            }}
          >
            {meta.icon}
          </div>
        </div>

        <div style={{ padding: '1.5rem 1.5rem 1.25rem', textAlign: 'center' }}>
          <h2
            id="confirm-modal-title"
            style={{
              margin: '0 0 0.75rem',
              fontSize: '1.2rem',
              fontWeight: 700,
              color: '#0f172a',
              lineHeight: 1.35,
            }}
          >
            {title}
          </h2>

          <p
            style={{
              margin: '0 0 0.75rem',
              fontSize: '0.95rem',
              lineHeight: 1.55,
              color: '#475569',
            }}
          >
            {message}
          </p>

          {highlight && (
            <div
              style={{
                margin: '0.75rem auto',
                padding: '0.6rem 0.9rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                fontSize: '0.9rem',
                color: '#1e293b',
                fontWeight: 500,
              }}
            >
              {highlight}
            </div>
          )}

          {warning && (
            <p
              style={{
                margin: '0.75rem 0 1.5rem',
                fontSize: '0.85rem',
                color: meta.accent,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span aria-hidden="true">⚠️</span>
              {warning}
            </p>
          )}

          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: warning ? '0' : '1.25rem',
            }}
          >
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                borderRadius: '0.625rem',
                border: '1.5px solid #e2e8f0',
                background: 'white',
                color: '#475569',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (loading) return;
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              {cancelLabel}
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                borderRadius: '0.625rem',
                border: 'none',
                background: meta.gradient,
                color: 'white',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                boxShadow: `0 4px 14px ${meta.ringColor}`,
                transition: 'transform 0.15s',
              }}
              onMouseEnter={(e) => {
                if (loading) return;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {loading ? '⏳ Siliniyor...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}